import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import {
  CONTENT_MASTER_FIELD_FILTERS_MAX,
  CONTENT_MASTER_FIELD_FILTER_VALUE_MAX,
  CONTENT_MASTER_IN_PROGRESS_TAB_STATUS,
  type ContentMasterFieldFilter,
  type ContentMasterListFilters,
  type ContentMasterProcessingStatusFilter,
  type ContentMasterSource,
} from '../store/slices/contentMasterSlice';
import { isContentMasterSourceLiteral } from './contentMasterWorkspace';

const ALLOWED_LIMITS = [10, 20, 50, 100] as const;

/**
 * Server-side validator accepts `source` only when it matches the composite
 * format `^(plattform|shop):\d+$`. Anything else (legacy numeric kShop,
 * stale `jtl` / `amazon` literals, hand-edited junk) is treated as "no
 * channel selected" so the workspace-config-driven default kicks in.
 */
const isValidSourceLiteral = (raw: string | null): raw is string =>
  raw !== null && isContentMasterSourceLiteral(raw);

export function parseContentMasterListSearchParams(
  searchParams: URLSearchParams,
  /**
   * When the URL has no `source` param (or the value is invalid), preserve
   * this fallback instead of returning `''`. The fallback is the channel id
   * the bootstrap (or a previous workspace-config fetch) already seeded into
   * Redux - without it, navigating to `/features/content-master` with no
   * query string would clobber the seeded source and the list-fetch effect
   * would silently bail out (CM "blank list on first visit" bug).
   *
   * Default `''` keeps the historical behaviour for callers that don't want
   * the fallback (e.g. URL canonicalization round-trip checks).
   */
  fallbackSource: ContentMasterSource = ''
): ContentMasterListFilters {
  const sourceRaw = searchParams.get('source');
  const source: ContentMasterSource = isValidSourceLiteral(sourceRaw)
    ? sourceRaw
    : fallbackSource;

  const langRaw = searchParams.get('language');
  const language = langRaw != null && langRaw.trim() !== '' ? langRaw.trim() : '';

  let page = parseInt(searchParams.get('page') || '1', 10);
  if (!Number.isFinite(page) || page < 1) {
    page = 1;
  }

  let limit = parseInt(searchParams.get('limit') || '20', 10);
  if (!ALLOWED_LIMITS.includes(limit as (typeof ALLOWED_LIMITS)[number])) {
    limit = 20;
  }

  const q = searchParams.get('q') ?? '';

  const statusRaw = searchParams.get('status');
  let processingStatus: ContentMasterProcessingStatusFilter = '';
  if (statusRaw === ContentMasterProductView.processingStatus.NEW) {
    // `new` tab = Wawi products not yet in our base for the current source.
    // Always a standalone value (never combined with other statuses).
    processingStatus = ContentMasterProductView.processingStatus.NEW;
  } else if (
    statusRaw === ContentMasterProductView.processingStatus.IN_PROGRESS ||
    statusRaw === CONTENT_MASTER_IN_PROGRESS_TAB_STATUS
  ) {
    // The In Progress tab also covers SCX listing drafts; a plain
    // `in_progress` from an old URL normalizes to the combined tab value.
    processingStatus = CONTENT_MASTER_IN_PROGRESS_TAB_STATUS;
  } else if (statusRaw === ContentMasterProductView.processingStatus.DONE) {
    processingStatus = ContentMasterProductView.processingStatus.DONE;
  }

  // `active` defaults to `true` (only active products). The param is only
  // present in the URL when the user turned the filter off, in which case
  // we expect the literal `false`.
  const active = searchParams.get('active') !== 'false';

  // Category filter: numeric id plus its display name (UI-only companion so
  // the chip survives a reload; never sent to the API).
  let kCategory: number | null = null;
  let kCategoryName: string | null = null;
  const kCategoryRaw = searchParams.get('kCategory');
  if (kCategoryRaw != null) {
    const parsed = parseInt(kCategoryRaw, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      kCategory = parsed;
      const nameRaw = searchParams.get('kCategoryName');
      kCategoryName = nameRaw != null && nameRaw !== '' ? nameRaw : null;
    }
  }
  // Wawi category filter (marketplace channels, next to the SCX `kCategory`).
  let kWawiCategory: number | null = null;
  let kWawiCategoryName: string | null = null;
  const kWawiCategoryRaw = searchParams.get('kWawiCategory');
  if (kWawiCategoryRaw != null) {
    const parsed = parseInt(kWawiCategoryRaw, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      kWawiCategory = parsed;
      const nameRaw = searchParams.get('kWawiCategoryName');
      kWawiCategoryName = nameRaw != null && nameRaw !== '' ? nameRaw : null;
    }
  }
  // Subtree toggle is bound to the categories - ignored without one.
  const includeSubcategories =
    (kCategory != null || kWawiCategory != null) &&
    searchParams.get('includeSubcategories') === 'true';

  // Field → phrase filters: repeated `ff=<field>:<phrase>` params. The field
  // key never contains `:` (core apiNames / wawiAttribut_N / wawiMerkmal_N /
  // mpAttr_*), so we split on the FIRST colon and the phrase may contain any
  // characters. Invalid entries (no colon, empty/oversized phrase) and rows
  // beyond the backend cap are dropped.
  const fieldFilters: ContentMasterFieldFilter[] = [];
  for (const raw of searchParams.getAll('ff')) {
    if (fieldFilters.length >= CONTENT_MASTER_FIELD_FILTERS_MAX) {
      break;
    }
    const sep = raw.indexOf(':');
    if (sep <= 0) {
      continue;
    }
    const field = raw.slice(0, sep);
    const value = raw.slice(sep + 1);
    if (value.trim() === '' || value.length > CONTENT_MASTER_FIELD_FILTER_VALUE_MAX) {
      continue;
    }
    fieldFilters.push({ field, value });
  }

  // Wawi flags default to off; only the literal `true` turns them on.
  const onlyInStock = searchParams.get('onlyInStock') === 'true';
  const onlyWithEan = searchParams.get('onlyWithEan') === 'true';
  const onlyNotActivated = searchParams.get('onlyNotActivated') === 'true';

  return {
    source,
    language,
    page,
    limit,
    q,
    processingStatus,
    active,
    kCategory,
    kCategoryName,
    kWawiCategory,
    kWawiCategoryName,
    includeSubcategories,
    fieldFilters,
    onlyInStock,
    onlyWithEan,
    onlyNotActivated,
  };
}

export function serializeContentMasterListFilters(filters: ContentMasterListFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.source) {
    p.set('source', filters.source);
  }
  if (filters.language.trim()) {
    p.set('language', filters.language.trim());
  }
  p.set('page', String(filters.page));
  p.set('limit', String(filters.limit));
  if (filters.q.trim()) {
    p.set('q', filters.q.trim());
  }
  if (filters.processingStatus) {
    p.set('status', filters.processingStatus);
  }
  // Only serialize when the filter is off (non-default), keeping clean URLs
  // for the common "only active products" case.
  if (!filters.active) {
    p.set('active', 'false');
  }
  if (filters.kCategory != null) {
    p.set('kCategory', String(filters.kCategory));
    if (filters.kCategoryName) {
      p.set('kCategoryName', filters.kCategoryName);
    }
  }
  if (filters.kWawiCategory != null) {
    p.set('kWawiCategory', String(filters.kWawiCategory));
    if (filters.kWawiCategoryName) {
      p.set('kWawiCategoryName', filters.kWawiCategoryName);
    }
  }
  if (
    (filters.kCategory != null || filters.kWawiCategory != null) &&
    filters.includeSubcategories
  ) {
    p.set('includeSubcategories', 'true');
  }
  for (const row of filters.fieldFilters) {
    // Only complete rows reach Redux (the panel commits on blur/enter), but
    // keep the guard so a half-filled row can never leak into the URL.
    if (row.field !== '' && row.value.trim() !== '') {
      p.append('ff', `${row.field}:${row.value}`);
    }
  }
  if (filters.onlyInStock) {
    p.set('onlyInStock', 'true');
  }
  if (filters.onlyWithEan) {
    p.set('onlyWithEan', 'true');
  }
  if (filters.onlyNotActivated) {
    p.set('onlyNotActivated', 'true');
  }
  return p;
}

/**
 * Full path to the Content Master products list with query string from filters
 * (used for `location.state` when navigating to a product so "back" restores
 * search tab, page, and query).
 */
export function buildContentMasterListPath(filters: ContentMasterListFilters): string {
  const qs = serializeContentMasterListFilters(filters).toString();
  return qs ? `/features/content-master?${qs}` : '/features/content-master';
}

/** Router state passed from the list page into product routes via `<Link state>`. */
export type ContentMasterListLocationState = {
  fromContentMasterList: true;
  contentMasterListReturn: string;
};

export function areContentMasterListFiltersEqual(
  a: ContentMasterListFilters,
  b: ContentMasterListFilters
): boolean {
  return (
    a.source === b.source &&
    a.language === b.language &&
    a.page === b.page &&
    a.limit === b.limit &&
    a.q === b.q &&
    a.processingStatus === b.processingStatus &&
    a.active === b.active &&
    a.kCategory === b.kCategory &&
    a.kCategoryName === b.kCategoryName &&
    a.kWawiCategory === b.kWawiCategory &&
    a.kWawiCategoryName === b.kWawiCategoryName &&
    a.includeSubcategories === b.includeSubcategories &&
    a.onlyInStock === b.onlyInStock &&
    a.onlyWithEan === b.onlyWithEan &&
    a.onlyNotActivated === b.onlyNotActivated &&
    areFieldFiltersEqual(a.fieldFilters, b.fieldFilters)
  );
}

function areFieldFiltersEqual(
  a: ContentMasterFieldFilter[],
  b: ContentMasterFieldFilter[]
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((row, i) => row.field === b[i].field && row.value === b[i].value);
}
